// ---- Decompose: "still running" is not "failed" ---------------------------
// Measured 6 Sep 2026 (CleanTech Grid Storage Platform, 30 documents): the
// synchronous POST /crosspollination/decompose ran 40.1 s in the Lambda while
// API Gateway closed the connection at its 29 s wall, so the browser saw
// "Request failed with status code 504" and the page read as a failure — yet
// the components were written and the matrix filled on the next reload.
// Roger's White-Space UX review (F1/F4 pattern): a UI must never report a
// failure the backend did not have. Until the route is made asynchronous
// (202 + poll, like /whitespace/scan), the client treats the gateway timeout
// as "still running" and re-reads the matrix until it fills.
export const DECOMPOSE_POLL_MS = 8_000
export const DECOMPOSE_POLL_MAX_MS = 4 * 60_000
export const DECOMPOSE_STILL_RUNNING_MESSAGE =
  'Feature extraction is still running on the server — it takes about a minute on a large project. The matrix will fill in here as soon as it finishes.'
export const DECOMPOSE_GAVE_UP_MESSAGE =
  'Extraction is taking longer than expected. Reload this tab in a minute — if the matrix is still empty, run Extract features again.'

/** True when the request died at the API Gateway 29-second wall or a client
 * timeout — the server keeps working, so the honest reading is "in progress". */
export function isDecomposeStillRunning(err: unknown): boolean {
  const e = err as { response?: { status?: number }; code?: string; message?: string } | null | undefined
  if (!e) return false
  if (e.response?.status === 504) return true
  if (e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT') return true
  return /status code 504|timeout/i.test(e.message ?? '')
}

/** Bug #5861: the route now answers 202 {status:'running'} and does the work
 * on a self-invoked worker; the matrix is re-read until it leaves
 * no_components. */
export function isDecomposeRunningResponse(res: unknown): boolean {
  return !!res && typeof res === 'object' && (res as { status?: unknown }).status === 'running'
}

/** The backend's own words for a real refusal; the still-running notice for a
 * timeout; a plain fallback otherwise. */
export function decomposeErrorMessage(err: unknown): string {
  if (isDecomposeStillRunning(err)) return DECOMPOSE_STILL_RUNNING_MESSAGE
  const e = err as { response?: { data?: { error?: string } | string }; message?: string } | null | undefined
  const data = e?.response?.data
  if (data && typeof data === 'object' && typeof data.error === 'string' && data.error) return data.error
  return e?.message || 'Failed to extract features.'
}

/** Roger's review F1: with many domains the grid is wider than the card and
 * the extra columns are invisible unless the viewer is told to scroll. */
export const MATRIX_SCROLL_HINT_MIN_DOMAINS = 6

/** Width of the sticky feature-label column (px). The label span is this
 * minus its right padding; the scroll container's scroll-padding-left is
 * exactly this, so a snapped domain column lands just right of the labels. */
export const MATRIX_LABEL_COLUMN_PX = 228
